// Métadonnées SEO des pages de prix de location, une par GPU ayant au moins un prix.
// Lues par vite.config.ts, entry-server (prerender, sitemap, IndexNow) et les pages.
// Pas de prix dans les titres et descriptions : ils seraient périmés entre deux
// déploiements, alors que la page, elle, se met à jour chaque jour.
// Extensions .ts et attribut JSON : vite.config.ts lit ce fichier (chargeur de config natif de Vite).
import SNAPSHOT from "../data/gpu-prices.json" with { type: "json" };
import { GPUS, validatePrices, type Gpu } from "../pages/llmVram.ts";
import { PRICE_SOURCE_SHORT } from "./priceSources.ts";
import { SITE, base, latestDay, seoTitle, type RouteSeo } from "./routes.ts";

const baseName = (g: Gpu) => g.label.split(" · ")[0];
// Cartes qui partagent un nom (A100 40 Go et 80 Go) : la mémoire est ajoutée pour les
// distinguer, sinon leurs pages auraient le même titre et le même H1.
const SHARED_NAMES = new Set(GPUS.map(baseName).filter((n, i, all) => all.indexOf(n) !== i));
export const gpuShort = (g: Gpu) => (SHARED_NAMES.has(baseName(g)) ? `${baseName(g)} ${g.vram}GB` : baseName(g));
/** Premier jour de l'historique quotidien (base SQLite du VPS). */
export const PRICE_TRACKING_START = "2026-10-05";
/** Jour des prix embarqués dans le build : le contenu prérendu des pages de prix en dépend. */
export const PRICES_DAY = (validatePrices(SNAPSHOT)?.updated ?? "1970-01-01").slice(0, 10);
export const gpuPricePath = (g: Gpu) => `/gpu-price/${g.id}`;
export const HOURS_PER_MONTH = 730;
export const usd = (x: number) => `$${x < 1 ? x.toFixed(3) : x.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const priced = new Set(Object.values(validatePrices(SNAPSHOT)?.providers ?? {}).flatMap((p) => Object.keys(p.prices)));
/** GPU ayant au moins un prix de location : seuls eux ont une page. */
export const PRICED_GPUS = GPUS.filter((g) => priced.has(g.id));

export const GPU_PRICE_SEO: Record<string, RouteSeo> = Object.fromEntries(
  PRICED_GPUS.map((g) => [
    gpuPricePath(g),
    {
      ...base,
      title: seoTitle(`${gpuShort(g)} Rental Price`, " per Hour, Updated Hourly"),
      description: `${gpuShort(g)}${gpuShort(g).endsWith("GB") ? "" : ` (${g.vram} GB)`} rental price: today's cheapest on-demand price per hour across ${PRICE_SOURCE_SHORT}, with price history.`,
      url: `${SITE}${gpuPricePath(g)}`,
      imageAlt: `${gpuShort(g)} rental price`,
      ogType: "article" as const,
      lastmod: latestDay("2026-10-06", PRICES_DAY),
      priority: 0.7,
    },
  ])
);
